import React, { useState } from 'react';
import { IoSave, IoCloudUpload, IoTrash, IoImage } from 'react-icons/io5';
import { University } from '@/types';
import ConfirmationModal from '@/components/ConfirmationModal';
import api from '@/utils/api.utils';
import { useQueryClient } from '@tanstack/react-query';

const SaveIcon = IoSave as React.ComponentType<{ className?: string }>;
const CloudUploadIcon = IoCloudUpload as React.ComponentType<{ className?: string }>;
const TrashIcon = IoTrash as React.ComponentType<{ className?: string }>;
const ImageIcon = IoImage as React.ComponentType<{ className?: string }>;

interface UniversityDetailsFormProps {
  university: University;
  formData: Partial<University>;
  onInputChange: (field: keyof University, value: any) => void;
  onSave: () => void;
  onDelete?: (universityId: number) => void;
  saving: boolean;
}

const UniversityDetailsForm: React.FC<UniversityDetailsFormProps> = ({
  university,
  formData,
  onInputChange,
  onSave,
  onDelete,
  saving,
}) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const queryClient = useQueryClient();

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = () => {
    setShowDeleteModal(false);
    if (onDelete) {
      onDelete(university.id);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
  };

  const handlePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB');
      return;
    }

    setPhotoFile(file);
    setUploadingPhoto(true);

    try {
      const formData = new FormData();
      formData.append('photo', file);
      const response = await api.post(`/api/v1/universities/${university.id}/upload-photo`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      const result = response.data;
      onInputChange('photo_url', result.photo_url);
      
      queryClient.invalidateQueries({ queryKey: ['universities-search'] });
      queryClient.invalidateQueries({ queryKey: ['universities'] });
      
      event.target.value = '';
      setPhotoFile(null);
      
    } catch (error: any) {
      console.error('Failed to upload photo:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to upload photo';
      alert(errorMessage);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handlePhotoDelete = async () => {
    if (!university.photo_url) return;

    setUploadingPhoto(true);
    try {
      await api.delete(`/api/v1/universities/${university.id}/photo`);
      
      onInputChange('photo_url', null);
      
      queryClient.invalidateQueries({ queryKey: ['universities-search'] });
      queryClient.invalidateQueries({ queryKey: ['universities'] });
      
    } catch (error: any) {
      console.error('Failed to delete photo:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to delete photo';
      alert(errorMessage);
    } finally {
      setUploadingPhoto(false);
    }
  };
  return (
    <div className="bg-white rounded-lg shadow-lg">
      <div className="p-6">
        <div className="mb-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">University Details</h3>
            <div className="flex items-center space-x-3">
              {onDelete && (
                <button
                  onClick={handleDeleteClick}
                  className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 flex items-center space-x-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  <span>Delete University</span>
                </button>
              )}
              <button
                onClick={onSave}
                disabled={saving}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
              >
                <SaveIcon className="text-lg" />
                <span>{saving ? 'Saving...' : 'Save All Changes'}</span>
              </button>
            </div>
          </div>

          <div className="mb-6 p-4 border border-gray-200 rounded-lg">
            <h4 className="text-md font-medium text-gray-900 mb-3 flex items-center">
              <ImageIcon className="mr-2 text-blue-600" />
              University Photo
            </h4>
            
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                {formData.photo_url ? (
                  <div className="relative">
                    <img
                      src={formData.photo_url}
                      alt={`${university.name} photo`}
                      className="w-32 h-24 object-cover rounded-lg border border-gray-300"
                    />
                    <button
                      onClick={handlePhotoDelete}
                      disabled={uploadingPhoto}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 disabled:opacity-50"
                      title="Delete photo"
                    >
                      <TrashIcon className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <div className="w-32 h-24 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
                    <ImageIcon className="w-8 h-8 text-gray-400" />
                  </div>
                )}
              </div>
              
              <div className="flex-1">
                <div className="relative">
                  <input
                    type="file"
                    id="photo-upload"
                    accept="image/*"
                    onChange={handlePhotoUpload}
                    disabled={uploadingPhoto}
                    className="hidden"
                  />
                  <label
                    htmlFor="photo-upload"
                    className={`inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer ${
                      uploadingPhoto ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    <CloudUploadIcon className="mr-2" />
                    {uploadingPhoto ? 'Uploading...' : 'Upload Photo'}
                  </label>
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Supported formats: JPG, PNG, GIF. Max size: 10MB. Images will be automatically resized to 800x600.
                </p>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <input
                type="text"
                value={formData.name || ''}
                onChange={(e) => onInputChange('name', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
              <input
                type="text"
                value={formData.location || ''}
                onChange={(e) => onInputChange('location', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Environment</label>
              <select
                value={formData.environment || ''}
                onChange={(e) => onInputChange('environment', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select...</option>
                <option value="Urban">Urban</option>
                <option value="Suburban">Suburban</option>
                <option value="Rural">Rural</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Undergraduate Students</label>
              <input
                type="number"
                value={formData.undergrad_students || ''}
                onChange={(e) => onInputChange('undergrad_students', parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Acceptance Rate (%)</label>
              <input
                type="number"
                step="0.1"
                value={formData.acceptance_rate || ''}
                onChange={(e) => onInputChange('acceptance_rate', parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Requires SAT/ACT</label>
              <select
                value={formData.sat_act_status || ''}
                onChange={(e) => onInputChange('sat_act_status', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select...</option>
                <option value="Required">Required</option>
                <option value="Optional">Optional</option>
                <option value="Not Required">Not Required</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Requires GPA</label>
              <select
                value={formData.high_school_gpa || ''}
                onChange={(e) => onInputChange('high_school_gpa', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select...</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Application Fee ($)</label>
              <input
                type="number"
                value={formData.application_fee || ''}
                onChange={(e) => onInputChange('application_fee', parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Early Decision</label>
              <select
                value={formData.early_decision_early_action || ''}
                onChange={(e) => onInputChange('early_decision_early_action', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select...</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Early Decision Deadline
              </label>
              <input
                type="date"
                value={formData.application_deadline || ''}
                onChange={(e) => onInputChange('application_deadline', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Application Deadline</label>
              <input
                type="date"
                value={formData.last_date_to_apply || ''}
                onChange={(e) => onInputChange('last_date_to_apply', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Net Price per Year ($)</label>
              <input
                type="number"
                value={formData.net_price_per_year || ''}
                onChange={(e) => onInputChange('net_price_per_year', parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Housing Price ($)</label>
              <input
                type="number"
                value={formData.average_housing_cost || ''}
                onChange={(e) => onInputChange('average_housing_cost', parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Meal Plan Price ($)</label>
              <input
                type="number"
                value={formData.average_meal_plan_cost || ''}
                onChange={(e) => onInputChange('average_meal_plan_cost', parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Books & Supplies ($)</label>
              <input
                type="number"
                value={formData.books_and_supplies || ''}
                onChange={(e) => onInputChange('books_and_supplies', parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">In State Tuition ($)</label>
              <input
                type="number"
                value={formData.in_state_tuition || ''}
                onChange={(e) => onInputChange('in_state_tuition', parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Out of State Tuition ($)</label>
              <input
                type="number"
                value={formData.out_of_state_tuition || ''}
                onChange={(e) => onInputChange('out_of_state_tuition', parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Average Financial Aid ($)</label>
              <input
                type="number"
                value={formData.average_total_aid_awarded || ''}
                onChange={(e) => onInputChange('average_total_aid_awarded', parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Students Receiving Aid (%)</label>
              <input
                type="number"
                value={formData.students_receiving_financial_aid || ''}
                onChange={(e) => onInputChange('students_receiving_financial_aid', parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Student-Faculty Ratio</label>
              <input
                type="text"
                value={formData.student_faculty_ratio || ''}
                onChange={(e) => onInputChange('student_faculty_ratio', parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Offers Online Courses</label>
              <select
                value={formData.offers_online_courses || ''}
                onChange={(e) => onInputChange('offers_online_courses', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select...</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Median Salary 5 Years ($)</label>
              <input
                type="number"
                value={formData.median_earnings_6_years_after_graduation || ''}
                onChange={(e) => onInputChange('median_earnings_6_years_after_graduation', parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Graduation Rate (%)</label>
              <input
                type="number"
                step="0.1"
                value={formData.graduation_rate || ''}
                onChange={(e) => onInputChange('graduation_rate', parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Employed After 1 Year (%)</label>
              <input
                type="number"
                step="0.1"
                value={formData.employed_2_years_after_graduation || ''}
                onChange={(e) => onInputChange('employed_2_years_after_graduation', parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>
      </div>

      <ConfirmationModal
        isOpen={showDeleteModal}
        title="Delete University"
        message="Are you sure you want to delete this university? This will also delete all supplements, prompts, and user associations."
        confirmText="OK"
        cancelText="Cancel"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        confirmButtonColor="red"
      />
    </div>
  );
};

export default UniversityDetailsForm;